import { doc, getDoc } from "firebase/firestore";

import { db } from "@/integrations/firebase/firestore";
import { normalizeQueryKey } from "@/routes/library-alias";
import { canReuseCompletedSearch } from "@/routes/report-source";

// Client-readable library membership: the alias map (query_key -> drug_id)
// plus `drugs/{id}.latest_version`. Mirrors the server's own serve order, so a
// hit here is exactly what the backend would answer from cache.

export type LibraryStatus = "ready" | "new";

async function readAliasDrugId(query: string): Promise<string | null> {
  const key = normalizeQueryKey(query);
  if (!key || key === "unknown") return null;
  const snap = await getDoc(doc(db, "drug_aliases", key));
  const drugId = snap.data()?.drug_id;
  return typeof drugId === "string" && drugId.length > 0 ? drugId : null;
}

async function readHasLibraryReport(drugId: string): Promise<boolean> {
  const snap = await getDoc(doc(db, "drugs", drugId));
  const latest = snap.data()?.latest_version as number | undefined;
  return canReuseCompletedSearch(latest);
}

export type LibraryLookup =
  | { kind: "hit"; drugId: string }
  | { kind: "miss" }
  | { kind: "error" };

// An offline SDK rejects getDoc at once while writes still queue, so one
// failed read is no evidence of a miss: a doc created on it would bypass a
// library report the alias map holds.
const LOOKUP_RETRY_DELAYS_MS = [500, 1500];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

// Only a successful read without a usable drug_id is a miss; the backend path,
// which also resolves synonyms and brands, takes over from there. `cancelled`
// stops the retries early once the caller no longer wants the answer.
export async function lookupLibraryDrugId(
  query: string,
  opts?: { cancelled?: () => boolean },
): Promise<LibraryLookup> {
  for (let attempt = 0; ; attempt++) {
    try {
      const drugId = await readAliasDrugId(query);
      return drugId ? { kind: "hit", drugId } : { kind: "miss" };
    } catch (error) {
      const delay = LOOKUP_RETRY_DELAYS_MS[attempt];
      if (delay === undefined) {
        console.error("Alias fast-path lookup failed:", error);
        return { kind: "error" };
      }
      if (opts?.cancelled?.()) return { kind: "error" };
      await sleep(delay);
      if (opts?.cancelled?.()) return { kind: "error" };
    }
  }
}

// An unreadable read counts as "no": a needless regeneration is cheap, whereas
// reattaching to a dead pointer strands the doctor.
export async function hasLibraryReport(drugId?: string): Promise<boolean> {
  if (!drugId) return false;
  try {
    return await readHasLibraryReport(drugId);
  } catch (error) {
    console.error("Library presence check failed:", error);
    return false;
  }
}

// Throws on a failed read, so a caller can show "unknown" instead of a false
// "new report" promise.
export async function resolveLibraryStatus(
  name: string,
): Promise<LibraryStatus> {
  const drugId = await readAliasDrugId(name);
  if (!drugId) return "new";
  return (await readHasLibraryReport(drugId)) ? "ready" : "new";
}
